import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import type {ColumnDef} from "@tanstack/react-table";
import type {HobexTransactionLog} from "crqlar-react-common/dist/models/integrations/hobex/hobexTransactionLog.model";
import {HobexConfigService} from "crqlar-react-common/dist/services/integrations/hobex/hobexConfig.service";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../../common/serverTable";
import HobexLogDetailsDialog from "./HobexLogDialog.component";

const formatProviderDescription = (providerData?: string) => {
    if (!providerData) return '-';

    try {
        const parsed = JSON.parse(providerData);
        const values: string[] = [];
        if (parsed?.id) values.push(`ID: ${parsed.id}`);
        if (parsed?.result?.code) values.push(`Result: ${parsed.result.code}`);
        if (parsed?.amount !== undefined) {
            values.push(`Amount: ${parsed.amount} ${parsed.currency || ''}`.trim());
        }
        if (parsed?.result?.description) values.push(`Description: ${parsed.result.description}`);
        return values.join('\n');
    } catch {
        return '';
    }
};

const HobexLogs = () => {
    const {t} = useTranslation();
    const [currentHobexLog, setCurrentHobexLog] = useState<HobexTransactionLog>();
    const columns = useMemo<ColumnDef<HobexTransactionLog>[]>(() => [
        {
            id: 'merchantTransactionId',
            header: t('Merchant Transaction ID'),
            accessorFn: log => log.merchantTransactionId,
            cell: ({row}) => <Box>{row.original.merchantTransactionId || ''}</Box>,
            size: 260,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'type',
            header: t('Transaction type'),
            accessorFn: log => log.transactionType,
            cell: ({row}) => (
                <Chip label={t(`Transaction type ${row.original.transactionType || 'UNKNOWN'}`)}/>
            ),
            size: 220,
            meta: {defaultActive: true, isServerSortable: true, serverSortField: 'transactionType'}
        },
        {
            id: 'status',
            header: t('Status'),
            accessorFn: log => log.success,
            cell: ({row}) => (
                <Box>{row.original.success === true ? 'OK' : row.original.success === false ? t('Failed') : '-'}</Box>
            ),
            size: 140,
            meta: {defaultActive: true, isServerSortable: true, serverSortField: 'success'}
        },
        {
            id: 'description',
            header: t('Description'),
            accessorFn: log => log.hobexProviderData,
            cell: ({row}) => (
                <Typography
                    variant="caption"
                    sx={{whiteSpace: 'pre-wrap', wordBreak: 'break-all', py: 0.5}}
                >
                    {formatProviderDescription(row.original.hobexProviderData)}
                </Typography>
            ),
            size: 420,
            meta: {defaultActive: true, grow: true}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: log => log.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 300,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: log => log.updateDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.updateDate)}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        }
    ], [t]);
    const controller = useServerTable({
        tableId: 'system-hobex-logs',
        columns,
        getRowId: log => log.id!,
        onRowClick: setCurrentHobexLog,
        initialSorting: [{id: 'id', desc: true}],
        queryFn: async ({pagination, searchQuery, sorting}) => {
            const page = await HobexConfigService.getLogs(
                pagination.pageIndex,
                pagination.pageSize,
                searchQuery || undefined,
                sorting
            );

            return {
                content: page.content ?? [],
                totalElements: page.totalElements ?? 0
            };
        }
    });

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', width: '100%', height: '70vh', minHeight: 0}}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar>
                        <ServerTableSearch/>
                        <ServerTableSort/>
                        <ServerTableFullscreen/>

                        <ServerTableColumns/>
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>

            <HobexLogDetailsDialog
                hobexLog={currentHobexLog}
                open={!!currentHobexLog}
                onClose={() => setCurrentHobexLog(undefined)}
            />
        </Box>
    );
};

export default HobexLogs;
